import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Paper,
  Stack,
  Group,
  Button,
  Table,
  Modal,
  TextInput,
  Select,
  Grid,
  Text,
  Center,
  Loader,
  Alert
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconEdit, IconTrash } from '@tabler/icons-react';
import { API_URL } from '../../utils/constants';
import '../admin/AccountStyles.css';

function SetupBarRatesScreen() {
  // BAR Rates state
  const [barRates, setBarRates] = useState([]);
  const [loadingBarRates, setLoadingBarRates] = useState(true);
  const [savingBarRate, setSavingBarRate] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBarRate, setSelectedBarRate] = useState(null);
  
  // BAR Rates Modal state
  const [showBarRateModal, setShowBarRateModal] = useState(false);
  const [barRateModalMode, setBarRateModalMode] = useState('add'); // 'add' or 'edit'
  const [barRateForm, setBarRateForm] = useState({
    rates_bar_header_id: '',
    unittypeid: '',
    date: '',
    rate: ''
  });
  const [barRateErrors, setBarRateErrors] = useState({});

  // Dropdown data
  const [barHeaders, setBarHeaders] = useState([]);
  const [unitTypes, setUnitTypes] = useState([]);

  // BAR Header filter for rates
  const [selectedBarHeaderFilter, setSelectedBarHeaderFilter] = useState(null);

  // Date range filter for rates
  const [dateRangeFilter, setDateRangeFilter] = useState({
    startDate: new Date().toISOString().split('T')[0], // Today
    endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // Today + 30 days
  });

  // Grid data state
  const [gridData, setGridData] = useState([]);
  const [gridDates, setGridDates] = useState([]);

  // Error and success messages
  const [message, setMessage] = useState({ text: '', variant: '', show: false });

  useEffect(() => {
    const checkBackend = async () => {
      try {
        const response = await fetch(`${API_URL}/health`);
        if (response.ok) {
          await fetchBarRates();
          await fetchBarHeaders();
          await fetchUnitTypes();
        } else {
          setMessage({ text: 'Backend server is not running. Please start the backend server.', variant: 'danger', show: true });
        }
      } catch (error) {
        console.error('Health check failed:', error);
        // Try to fetch unit types anyway, in case the health check is the issue
        await fetchUnitTypes();
        setMessage({ text: 'Cannot connect to backend server. Please check if it is running.', variant: 'danger', show: true });
      }
    };

    checkBackend();
  }, []);

  // Fetch BAR Rates
  const fetchBarRates = async () => {
    try {
      setLoadingBarRates(true);
      const response = await fetch(`${API_URL}/rates-bar`);
      if (!response.ok) throw new Error('Failed to fetch BAR rates');
      const data = await response.json();
      console.log('Fetched BAR rates:', data);
      if (data.length > 0) {
        console.log('First rate example:', data[0]);
        console.log('Rate value type:', typeof data[0].rate, 'Value:', data[0].rate);
      }
      setBarRates(data);
    } catch (error) {
      console.error('Error fetching BAR rates:', error);
      setMessage({ text: 'Failed to load BAR rates', variant: 'danger', show: true });
    } finally {
      setLoadingBarRates(false);
    }
  };

  // Fetch BAR Headers
  const fetchBarHeaders = async () => {
    try {
      const response = await fetch(`${API_URL}/rates-bar-header`);
      if (!response.ok) throw new Error('Failed to fetch BAR headers');
      const data = await response.json();
      setBarHeaders(data);
    } catch (error) {
      console.error('Error fetching BAR headers:', error);
    }
  };

  // Fetch Unit Types
  const fetchUnitTypes = async () => {
    try {
      console.log('Fetching unit types from:', `${API_URL}/unit-types`); // Debug log
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(`${API_URL}/unit-types`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      console.log('Unit types response status:', response.status); // Debug log
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Unit types response error:', errorText); // Debug log
        throw new Error(`Failed to fetch unit types: ${response.status} ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Fetched unit types:', data); // Debug log
      console.log('First unit type structure:', data[0]); // Debug log to see the actual structure
      setUnitTypes(data);
    } catch (error) {
      console.error('Error fetching unit types:', error);
      if (error.name === 'AbortError') {
        setMessage({ text: 'Request timed out. Please check your connection and try again.', variant: 'danger', show: true });
      } else {
        setMessage({ text: `Failed to load unit types: ${error.message}`, variant: 'danger', show: true });
      }
    }
  };

  // Create BAR Rate
  const createBarRate = async (barRateData) => {
    try {
      console.log('Creating BAR rate with data:', barRateData);
      const response = await fetch(`${API_URL}/rates-bar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(barRateData)
      });
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error response data:', errorData);
        throw new Error(errorData.message || 'Failed to create BAR rate');
      }
      
      const newBarRate = await response.json();
      console.log('Created BAR rate:', newBarRate);
      setBarRates([...barRates, newBarRate]);
      setMessage({ text: 'BAR rate created successfully', variant: 'success', show: true });
      return newBarRate;
    } catch (error) {
      console.error('Error creating BAR rate:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
      throw error;
    }
  };

  // Update BAR Rate
  const updateBarRate = async (id, updates) => {
    try {
      const response = await fetch(`${API_URL}/rates-bar/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update BAR rate');
      }
      
      const updatedRate = await response.json();
      setBarRates(barRates.map(r => r.id === id ? updatedRate : r));
      setMessage({ text: 'BAR rate updated successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error updating BAR rate:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
      throw error;
    }
  };

  // Delete BAR Rate
  const handleDeleteBarRate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this BAR rate?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/rates-bar/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete BAR rate');
      }
      
      setBarRates(barRates.filter(r => r.id !== id));
      setMessage({ text: 'BAR rate deleted successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error deleting BAR rate:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    }
  };

  // Modal handlers
  const handleAddBarRate = () => {
    setBarRateModalMode('add');
    setSelectedBarRate(null);
    setBarRateForm({ 
      rates_bar_header_id: '', 
      unittypeid: '', 
      date: '', 
      rate: '' 
    });
    setBarRateErrors({});
    setShowBarRateModal(true);
  };

  const handleEditBarRate = (barRate) => {
    setBarRateModalMode('edit');
    setSelectedBarRate(barRate);
    setBarRateForm({ 
      rates_bar_header_id: barRate.rates_bar_header_id?.toString() || '', 
      unittypeid: barRate.unittypeid?.toString() || '', 
      date: barRate.date || '', 
      rate: barRate.rate?.toString() || '' 
    });
    setBarRateErrors({});
    setShowBarRateModal(true);
  };

  const handleCloseBarRateModal = () => {
    setShowBarRateModal(false);
    setSelectedBarRate(null);
    setBarRateForm({ 
      rates_bar_header_id: '', 
      unittypeid: '', 
      date: '', 
      rate: '' 
    });
    setBarRateErrors({});
  };

  const handleBarRateInputChange = (field, value) => {
    setBarRateForm(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (barRateErrors[field]) {
      setBarRateErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSaveBarRate = async () => {
    // Validation
    const errors = {};
    if (!barRateForm.rates_bar_header_id) {
      errors.rates_bar_header_id = 'BAR Header is required';
    }
    if (!barRateForm.unittypeid) {
      errors.unittypeid = 'Unit Type is required';
    }
    if (!barRateForm.date) {
      errors.date = 'Date is required';
    }
    if (!barRateForm.rate || parseFloat(barRateForm.rate) <= 0) {
      errors.rate = 'Valid rate is required';
    }
    
    if (Object.keys(errors).length > 0) {
      setBarRateErrors(errors);
      return;
    }

    try {
      setSavingBarRate(true);
      
      const barRateData = {
        ...barRateForm,
        rates_bar_header_id: parseInt(barRateForm.rates_bar_header_id),
        unittypeid: parseInt(barRateForm.unittypeid),
        rate: parseFloat(barRateForm.rate)
      };
      
      console.log('Form data before processing:', barRateForm);
      console.log('Processed data for API:', barRateData);
      
      if (barRateModalMode === 'add') {
        await createBarRate(barRateData);
      } else {
        await updateBarRate(selectedBarRate.id, barRateData);
      }
      
      handleCloseBarRateModal();
    } catch (error) {
      // Error already handled in the API functions
    } finally {
      setSavingBarRate(false);
    }
  };

  // Helper function to get BAR header name by ID
  const getBarHeaderName = (headerId) => {
    const header = barHeaders.find(h => h.id === headerId);
    return header ? header.bar : 'Unknown';
  };

  // Helper function to get unit type name by ID
  const getUnitTypeName = (unitTypeId) => {
    const unitType = unitTypes.find(ut => ut.id === unitTypeId);
    return unitType ? unitType.unit_type : 'Unknown';
  };

  // Generate date range for grid columns
  const generateDateRange = () => {
    const dates = [];
    const startDate = new Date(dateRangeFilter.startDate);
    const endDate = new Date(dateRangeFilter.endDate);
    
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      dates.push(new Date(d).toISOString().split('T')[0]);
    }
    return dates;
  };

  // Computed filtered rates based on selected header and date range
  const filteredBarRates = barRates.filter(rate => {
    // Filter by BAR Header if selected
    if (selectedBarHeaderFilter && rate.rates_bar_header_id !== selectedBarHeaderFilter) {
      return false;
    }
    
    // Filter by date range
    const rateDate = new Date(rate.date);
    const startDate = new Date(dateRangeFilter.startDate);
    const endDate = new Date(dateRangeFilter.endDate);
    
    return rateDate >= startDate && rateDate <= endDate;
  });

  // Generate grid data for display
  const generateGridData = () => {
    const dates = generateDateRange();
    const headers = selectedBarHeaderFilter ? [barHeaders.find(h => h.id === selectedBarHeaderFilter)] : barHeaders;
    const gridData = [];

    console.log('Generating grid with:', { 
      dates: dates.length, 
      headers: headers.length, 
      unitTypes: unitTypes.length,
      barHeaders: barHeaders.length 
    }); // Debug log

    headers.forEach(header => {
      unitTypes.forEach(unitType => {
        const row = {
          barHeaderId: header.id,
          barHeaderName: header.bar,
          unitTypeId: unitType.id,
          unitTypeName: unitType.unit_type,
          dates: {}
        };

        // Initialize all dates with empty values
        dates.forEach(date => {
          row.dates[date] = '';
        });

        // Fill in existing rates
        filteredBarRates.forEach(rate => {
          if (rate.rates_bar_header_id === header.id && rate.unittypeid === unitType.id) {
            const rateDate = new Date(rate.date).toISOString().split('T')[0];
            if (row.dates.hasOwnProperty(rateDate)) {
              // Ensure rate is displayed as a string to preserve the exact value
              const rateValue = rate.rate.toString();
              console.log('Loading rate:', { rate: rate.rate, asString: rateValue, date: rateDate });
              row.dates[rateDate] = rateValue;
            }
          }
        });

        gridData.push(row);
      });
    });

    console.log('Generated grid data:', gridData); // Debug log
    return { gridData, dates };
  };

  // Generate grid data when dependencies change
  useEffect(() => {
    const { gridData: newGridData, dates: newDates } = generateGridData();
    setGridData(newGridData);
    setGridDates(newDates);
  }, [barHeaders, unitTypes, selectedBarHeaderFilter, dateRangeFilter]);

  // Handle inline rate editing
  const handleInlineRateChange = async (barHeaderId, unitTypeId, date, newRate) => {
    if (!newRate || newRate === '') return;

    console.log('Saving rate:', { newRate, type: typeof newRate, parsed: parseFloat(newRate) });

    try {
      // Check if rate already exists
      const existingRate = barRates.find(rate => 
        rate.rates_bar_header_id === barHeaderId && 
        rate.unittypeid === unitTypeId && 
        new Date(rate.date).toISOString().split('T')[0] === date
      );

      if (existingRate) {
        // Update existing rate
        await updateBarRate(existingRate.id, { rate: parseFloat(newRate) });
      } else {
        // Create new rate
        await createBarRate({
          rates_bar_header_id: barHeaderId,
          unittypeid: unitTypeId,
          date: date,
          rate: parseFloat(newRate)
        });
      }
      
      // Show success message
      setMessage({ text: 'Rate saved successfully!', variant: 'success', show: true });
    } catch (error) {
      console.error('Error saving inline rate:', error);
      setMessage({ text: 'Failed to save rate', variant: 'danger', show: true });
    }
  };

  // Filter BAR rates based on search term
  const searchFilteredBarRates = barRates.filter(rate => {
    const barHeaderName = getBarHeaderName(rate.rates_bar_header_id);
    const unitTypeName = getUnitTypeName(rate.unittypeid);
    const searchLower = searchTerm.toLowerCase();
    
    return barHeaderName.toLowerCase().includes(searchLower) ||
           unitTypeName.toLowerCase().includes(searchLower) ||
           rate.date?.toLowerCase().includes(searchLower) ||
           rate.rate?.toString().includes(searchLower);
  });

  // Handle row selection
  const handleRowSelect = (rate) => {
    setSelectedBarRate(rate);
  };

  return (
    <Container fluid className="account-screen p-0">
      <div className="d-flex">
        {/* Main Content */}
        <div className="main-content" style={{ flex: 1, padding: '2rem', maxWidth: '100%', overflow: 'hidden' }}>
          <Stack gap="md">
            <Group justify="space-between">
              <Title order={2}>Best Available Rates</Title>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={handleAddBarRate}
              >
                Add BAR Rate
              </Button>
            </Group>

            {message.show && (
              <Alert color={message.variant === 'danger' ? 'red' : message.variant === 'success' ? 'green' : 'blue'}>
                {message.text}
              </Alert>
            )}

            {/* Filter Section */}
            <Paper p="md" style={{ maxWidth: '100%', overflow: 'hidden' }}>
              <Stack gap="md">
                <Grid>
                  <Grid.Col span={4}>
                    <Select
                      label="Filter by BAR Header"
                      placeholder="All BAR Headers"
                      value={selectedBarHeaderFilter?.toString() || ''}
                      onChange={(value) => setSelectedBarHeaderFilter(value ? parseInt(value) : null)}
                      data={[
                        { value: '', label: 'All BAR Headers' },
                        ...barHeaders.map(header => ({ value: header.id.toString(), label: header.bar }))
                      ]}
                    />
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <TextInput
                      label="Start Date"
                      type="date"
                      value={dateRangeFilter.startDate}
                      onChange={(e) => setDateRangeFilter(prev => ({ ...prev, startDate: e.target.value }))}
                    />
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <TextInput
                      label="End Date"
                      type="date"
                      value={dateRangeFilter.endDate}
                      onChange={(e) => setDateRangeFilter(prev => ({ ...prev, endDate: e.target.value }))}
                    />
                  </Grid.Col>
                </Grid>
                
                <Group justify="space-between">
                  <TextInput
                    placeholder="Search BAR rates..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: 300 }}
                  />
                </Group>
                
                {loadingBarRates ? (
                  <Center py="xl">
                    <Loader size="md" />
                    <Text ml="md">Loading BAR rates...</Text>
                  </Center>
                ) : gridData.length === 0 ? (
                  <Alert color="orange" title="No BAR Rates Found">
                    {unitTypes.length === 0 ? (
                      <>
                        <Text>No unit types found. Please create the unit types table first.</Text>
                        <Button 
                          variant="light" 
                          onClick={() => window.location.href = '/dashboard/setup-units'}
                          mt="md"
                        >
                          Go to Unit Types Setup
                        </Button>
                      </>
                    ) : barHeaders.length === 0 ? (
                      <>
                        <Text>No BAR headers found. Please add BAR headers first.</Text>
                        <Button 
                          variant="light" 
                          onClick={() => window.location.href = '/dashboard/setup-bar-headers'}
                          mt="md"
                        >
                          Add BAR Headers
                        </Button>
                      </>
                    ) : (
                      <Text>No data available. Please check your configuration.</Text>
                    )}
                  </Alert>
                ) : (
                  <div style={{ 
                    maxHeight: '600px', 
                    overflowY: 'auto', 
                    overflowX: 'auto',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    width: '100%'
                  }}>
                    <Table striped style={{ width: '100%' }}>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>BAR Header</Table.Th>
                          <Table.Th>Unit Type</Table.Th>
                          {gridDates.map(date => (
                            <Table.Th key={date}>
                              {new Date(date).getMonth() + 1}/{new Date(date).getDate()}/{new Date(date).getFullYear().toString().slice(-2)}
                            </Table.Th>
                          ))}
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {gridData.map((row, rowIndex) => (
                          <Table.Tr key={`${row.barHeaderId}-${row.unitTypeId}`}>
                            <Table.Td>
                              <Text fw={500}>{row.barHeaderName}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">{row.unitTypeName}</Text>
                            </Table.Td>
                            {gridDates.map(date => (
                              <Table.Td key={date}>
                                <TextInput
                                  size="sm"
                                  value={row.dates[date] || ''}
                                  onChange={(e) => {
                                    const newRate = e.target.value;
                                    // Allow only numbers, decimal points, and backspace
                                    if (newRate && !/^\d*\.?\d{0,2}$/.test(newRate)) {
                                      return;
                                    }
                                    // Validate rate is within acceptable range
                                    if (newRate && parseFloat(newRate) > 99999.99) {
                                      notifications.show({
                                        title: 'Invalid Rate',
                                        message: 'Rate cannot exceed $99,999.99',
                                        color: 'red'
                                      });
                                      return;
                                    }
                                    // Update local state immediately for responsive UI
                                    const updatedGridData = [...gridData];
                                    updatedGridData[rowIndex].dates[date] = newRate;
                                    setGridData(updatedGridData);
                                  }}
                                  onBlur={(e) => {
                                    // Save the rate when user changes focus
                                    const newRate = e.target.value;
                                    if (newRate && parseFloat(newRate) > 0) {
                                      // Small delay to ensure user has finished typing
                                      setTimeout(() => {
                                        handleInlineRateChange(row.barHeaderId, row.unitTypeId, date, newRate);
                                      }, 100);
                                    }
                                  }}
                                  style={{ width: '80px', textAlign: 'center' }}
                                />
                              </Table.Td>
                            ))}
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </div>
                )}
              </Stack>
            </Paper>
          </Stack>
        </div>
      </div>

      {/* BAR Rate Modal */}
      <Modal 
        opened={showBarRateModal} 
        onClose={handleCloseBarRateModal}
        title={barRateModalMode === 'add' ? 'Add BAR Rate' : 'Edit BAR Rate'}
        size="md"
      >
        <Stack gap="md">
          <Grid>
            <Grid.Col span={6}>
              <Select
                label="BAR Header"
                placeholder="Select BAR Header"
                value={barRateForm.rates_bar_header_id?.toString() || ''}
                onChange={(value) => handleBarRateInputChange('rates_bar_header_id', value ? parseInt(value) : '')}
                data={barHeaders.map(header => ({ value: header.id.toString(), label: header.bar }))}
                error={barRateErrors.rates_bar_header_id}
                required
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <Select
                label="Unit Type"
                placeholder="Select Unit Type"
                value={barRateForm.unittypeid?.toString() || ''}
                onChange={(value) => handleBarRateInputChange('unittypeid', value ? parseInt(value) : '')}
                data={unitTypes.map(unitType => ({ value: unitType.id.toString(), label: unitType.unit_type }))}
                error={barRateErrors.unittypeid}
                required
              />
            </Grid.Col>
          </Grid>
          
          <Grid>
            <Grid.Col span={6}>
              <TextInput
                label="Date"
                type="date"
                value={barRateForm.date}
                onChange={(e) => handleBarRateInputChange('date', e.target.value)}
                error={barRateErrors.date}
                required
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Rate ($)"
                placeholder="$0.00"
                value={barRateForm.rate}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow only numbers, decimal points, and backspace
                  if (value && !/^\d*\.?\d{0,2}$/.test(value)) {
                    return;
                  }
                  // Validate rate is within acceptable range
                  if (value && parseFloat(value) > 99999.99) {
                    notifications.show({
                      title: 'Invalid Rate',
                      message: 'Rate cannot exceed $99,999.99',
                      color: 'red'
                    });
                    return;
                  }
                  handleBarRateInputChange('rate', value);
                }}
                error={barRateErrors.rate}
                required
              />
            </Grid.Col>
          </Grid>

          <Group justify="flex-end" mt="md">
            <Button variant="outline" onClick={handleCloseBarRateModal}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveBarRate}
              loading={savingBarRate}
            >
              {savingBarRate ? 'Saving...' : 'Save'}
            </Button>
          </Group>
        </Stack>
      </Modal>
      
      <style>{`
        /* Ensure main content doesn't overflow */
        .main-content {
          max-width: 100vw !important;
          overflow-x: hidden !important;
        }
        
        /* Responsive table container */
        .main-content div[style*="overflowX"] {
          overflow-x: auto !important;
          overflow-y: auto;
          width: 100% !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
        
        /* Custom scrollbar styling for the rates grid */
        .main-content div[style*="overflowX"]::-webkit-scrollbar {
          height: 12px;
        }
        
        .main-content div[style*="overflowX"]::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 6px;
        }
        
        .main-content div[style*="overflowX"]::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 6px;
        }
        
        .main-content div[style*="overflowX"]::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
        
        /* Responsive table styling */
        .main-content div[style*="overflowX"] table {
          width: 100%;
          table-layout: fixed;
        }
        
        /* Column styling - make them smaller */
        .main-content div[style*="overflowX"] table th,
        .main-content div[style*="overflowX"] table td {
          min-width: 60px;
          max-width: 100px;
          padding: 4px;
          text-align: center;
          font-size: 12px;
        }
        
        /* Input styling within table cells */
        .main-content div[style*="overflowX"] table td input {
          width: 100%;
          max-width: 80px;
          text-align: center;
          font-size: 12px;
        }
        
        /* Force the container to respect viewport width */
        .account-screen {
          max-width: 100vw !important;
          overflow-x: hidden !important;
        }
      `}</style>
    </Container>
  );
}

export default SetupBarRatesScreen;